import { useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { useFragment } from '@apollo/client';
import queryString from 'query-string';

import { dialogDetails, login } from '@urls';
import { useIsAuth } from '@hook/useIsAuth';
import { useAppContext } from '@contexts/AppContext';
import { getShafaAbsoluteUrl } from '@utils/common';
import { useCreateProductDialog } from '@hook/useCreateProductDialog';
import { ProductDetailsQuestion } from '@components/product/ProductDetailsQuestion';
import { FormData, ProductDetailsQuestionFormModal } from '@components/product/ProductDetailsQuestionFormModal';

import {
  ProductDetailsQuestionProductFragment,
  ProductDetailsQuestionProductFragmentDoc,
  useProductDetailsQuestionQuery,
} from './gql/graphql.v3.generated';

type Product = {
  __typename?: 'ProductDetails',
  id: number,
}

interface Props {
  className?: string,
  product?: Product,
}

export const ProductDetailsQuestionContainer: React.FC<Props> = ({
  className,
  product,
}) => {
  const history = useHistory();
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  const isAuth = useIsAuth();
  const { isOutlet } = useAppContext();

  const { data: productData, complete: productDataComplete } = useFragment<ProductDetailsQuestionProductFragment>({
    fragment: ProductDetailsQuestionProductFragmentDoc,
    from: product || null,
  });

  const { data, loading } = useProductDetailsQuestionQuery({
    variables: {
      dialogId: productData.dialogId,
      skipFetchThread: !productData.dialogId,
    },
    context: {
      batch: true,
    },
    skip: !isAuth,
  });

  const handleCreateDialog = useCreateProductDialog();

  const viewer = data?.viewer;

  const handleClick = () => {
    if (!viewer) {
      const loginPath = history.createHref({
        pathname: login,
        search: queryString.stringify({
          next: location.pathname,
        }),
      });

      window.location.href = loginPath;
    } else if (viewer?.thread) {
      const dialogPathname = history.createHref({
        pathname: generatePath(dialogDetails, { threadId: viewer?.thread.threadId }),
      });

      if (isOutlet) {
        window.location.href = getShafaAbsoluteUrl(
          dialogPathname,
        )
      } else {
        window.location.href = dialogPathname;
      }
    } else {
      setIsFormModalOpen(true);
    }
  };

  const handleSubmit = async (formData: FormData) => {
    if (!formData.body || !productData.id) {
      return null;
    }

    const errors = await handleCreateDialog({
      productId: productData.id,
      body: formData.body,
    });

    return errors;
  };

  return (
    <>
      <ProductDetailsQuestion
        className={className}
        disabled={loading}
        onClick={handleClick}
      />

      {!!productDataComplete && !!viewer && (
        <ProductDetailsQuestionFormModal
          isOpen={isFormModalOpen}
          product={productData}
          user={viewer}
          onSubmit={handleSubmit}
          onClose={() => setIsFormModalOpen(false)}
        />
      )}
    </>
  );
};
